// Winback campaign analytics, INTERNAL surface.
//
// This is the disposable test UI, not the client-facing Tenant Admin dashboard.
// That dashboard is deliberately unbuilt: the addressable universe is currently a
// few dozen customers, and a client surface reporting three sends and a
// single-digit match rate costs more trust than no surface. This panel exists so
// the plumbing can be seen working end to end.
//
// Layout follows docs/winback-analytics-mockup.html, which is approved for layout.
// Two of its sections - the per-customer table and the recommendation-rank
// breakdown - need gold_winback_performance at per-send grain, which this endpoint
// does not serve. They are omitted rather than faked.
//
// THE ONE IDEA THIS SCREEN IS BUILT AROUND: a stage that cannot be measured is
// drawn as measurably-unknown, never as zero. Flow attribution did not exist
// before a known date, so on earlier campaign days "0 delivered" means "we cannot
// tell", not "the mail failed". Every funnel chart draws that as an empty bar,
// which turns a measurement gap into a fabricated campaign failure. Here it
// hatches the track and says so.

import { useCallback, useEffect, useState } from "react";
import { ApiHttpError, type ApiConfig, getWinbackCampaigns } from "../api";
import type { WinbackCampaignRow, WinbackCampaignsResponse, WinbackTotals } from "../types";

const WINDOW_OPTIONS = [7, 30, 90, 365];

/** A rate of null means the denominator was zero. Rendering that as "0%" turns
 *  "nothing was delivered" into "nobody opened it", so null gets a dash. */
function pct(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return `${(value * 100).toFixed(1)}%`;
}

function money(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  // Fixed two decimals. Without a minimum, 5670.9 renders "$5,670.9" next to
  // "$24,368.25" and the column stops lining up.
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function num(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString();
}

function dayCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  return `${Math.round(value)}d`;
}

/** Whether the window contains any flow-attributed engagement at all.
 *
 *  Mail was pushed but not one delivery, open, click, or bounce came back. Since
 *  engagement is joined on Klaviyo flow id, that is the signature of campaign days
 *  that predate flow attribution - the events exist in Klaviyo, we simply cannot
 *  prove they were ours. Treating it as real zero engagement would report a
 *  deliverability catastrophe that did not happen. */
function engagementIsMeasurable(t: WinbackTotals): boolean {
  if (t.pushed === 0) return true; // nothing sent: nothing to attribute either way
  return t.delivered > 0 || t.opened > 0 || t.clicked > 0 || t.bounced > 0;
}

/** The same question for a single campaign day.
 *
 *  Applied per row as well as to the totals, because a table printing "0
 *  delivered" beside a funnel saying "not measurable" contradicts itself - and
 *  the whole point of this screen is that the two are different claims. */
function rowEngagementIsMeasurable(r: WinbackCampaignRow): boolean {
  if (!r.pushed) return true;
  return Boolean(r.delivered || r.opened || r.clicked || r.bounced);
}

/** A count that is only knowable when engagement was attributable. */
function attributed(value: number | null | undefined, measurable: boolean): string {
  return measurable ? num(value) : "n/a";
}

interface Stage {
  name: string;
  value: number | null;
  /** null = cannot be measured, distinct from a value of 0. */
  measurable: boolean;
  tone?: "end";
}

function funnelOf(t: WinbackTotals, measurable: boolean): Stage[] {
  return [
    { name: "Predicted churners", value: t.customers_eligible, measurable: true },
    { name: "Matched to a profile", value: t.customers_matched, measurable: true },
    { name: "Emailed", value: t.pushed, measurable: true },
    { name: "Delivered", value: t.delivered, measurable },
    { name: "Opened by a human", value: t.opened_human, measurable },
    { name: "Clicked", value: t.clicked, measurable },
    { name: "Came back", value: t.reactivated, measurable: true, tone: "end" },
  ];
}

/** Actionable segments, derived from the aggregate counts. */
function segmentsOf(t: WinbackTotals, measurable: boolean) {
  return [
    {
      name: "Never delivered",
      hint: "Deliverability or suppression",
      value: Math.max(0, t.pushed - t.delivered),
      measurable,
    },
    { name: "Bounced", hint: "Address or reputation", value: t.bounced, measurable },
    {
      name: "Not opened",
      hint: "Subject line, or genuinely gone",
      value: Math.max(0, t.delivered - t.opened_human),
      measurable,
    },
    {
      name: "Opened, no click",
      hint: "Copy or offer",
      value: Math.max(0, t.opened_human - t.clicked),
      measurable,
    },
    {
      name: "Clicked, no purchase",
      hint: "Highest intent - retarget",
      value: Math.max(0, t.clicked - t.reactivated),
      measurable,
      hot: true,
    },
    {
      name: "Came back",
      hint: money(t.attributed_revenue),
      value: t.reactivated,
      measurable: true,
    },
  ];
}

export function WinbackPanel({ config }: { config: ApiConfig }) {
  const [data, setData] = useState<WinbackCampaignsResponse | null>(null);
  const [windowDays, setWindowDays] = useState(30);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getWinbackCampaigns(config, { days: windowDays }));
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiHttpError
          ? `${e.status} ${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setLoading(false);
    }
  }, [config, windowDays]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <div className="error-banner">{error}</div>;
  if (!data) {
    return <div className="wb-empty">{loading ? "Loading winback analytics" : ""}</div>;
  }

  const { totals, lift, campaigns, caveats } = data;
  const measurable = engagementIsMeasurable(totals);
  const top = totals.customers_eligible || 1;
  const byStrategy = groupByStrategy(campaigns);

  return (
    <div className="wb">
      <div className="wb-bar">
        <span className="wb-title">Winback</span>
        <span className="wb-meta">
          {totals.campaign_days} campaign {totals.campaign_days === 1 ? "day" : "days"}
          {" · "}
          {num(totals.pushed)} emailed
        </span>
        <span className="wb-spacer" />
        <div className="wb-wins" role="group" aria-label="Time window">
          {WINDOW_OPTIONS.map((d) => (
            <button
              key={d}
              className="wb-win"
              aria-pressed={d === windowDays}
              onClick={() => setWindowDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {data.note && <div className="wb-note">{data.note}</div>}

      <div className="wb-kpis">
        <Kpi label="Emailed" value={num(totals.pushed)} sub={`${num(totals.customers_matched)} matched`} />
        <Kpi
          label="Delivered"
          value={measurable ? num(totals.delivered) : "n/a"}
          sub={measurable ? pct(totals.delivery_rate) : "not attributable"}
        />
        <Kpi
          label="Clicked"
          value={measurable ? num(totals.clicked) : "n/a"}
          sub={measurable ? `${pct(totals.click_rate)} of delivered` : "not attributable"}
        />
        <Kpi
          label="Came back"
          value={num(totals.reactivated)}
          sub={`${pct(totals.reactivation_rate)} of targeted - a floor`}
        />
        <Kpi
          label="Lift"
          hero
          value={
            lift.available && lift.lift_pp !== null && lift.lift_pp !== undefined
              ? `${lift.lift_pp >= 0 ? "+" : ""}${lift.lift_pp.toFixed(1)}pp`
              : "-"
          }
          sub={
            lift.available
              ? lift.underpowered
                ? "directional only"
                : `vs ${pct(lift.control_reactivation_rate)} held back`
              : "no holdout yet"
          }
        />
        <Kpi
          label="Incremental revenue"
          value={money(lift.available ? lift.incremental_revenue : null)}
          sub={`${money(totals.attributed_revenue)} attributed`}
        />
      </div>

      {lift.reason && <div className="wb-note">{lift.reason}</div>}

      {/* The signature element. */}
      <section className="wb-card">
        <div className="wb-card-hd">
          <h2>Where customers drop out</h2>
          <span className="wb-card-hint">
            share of {num(totals.customers_eligible)} predicted churners
          </span>
        </div>
        <div className="wb-card-bd">
          <div className="wb-fnl">
            {funnelOf(totals, measurable).map((s) => {
              const unknown = !s.measurable;
              const width = unknown ? 0 : Math.min(100, ((s.value ?? 0) / top) * 100);
              return (
                <div className="wb-fnl-row" key={s.name}>
                  <span className="wb-fnl-nm">{s.name}</span>
                  <span className={unknown ? "wb-fnl-trk unknown" : "wb-fnl-trk"}>
                    {!unknown && (
                      <span
                        className={s.tone === "end" ? "wb-fnl-fill end" : "wb-fnl-fill"}
                        style={{ width: `${width}%` }}
                      />
                    )}
                  </span>
                  {unknown ? (
                    <span className="wb-fnl-val unknown">not measurable</span>
                  ) : (
                    <span className="wb-fnl-val">
                      {num(s.value)}
                      <span className="pc">{pct((s.value ?? 0) / top)}</span>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="wb-fnl-legend">
            <span>
              <span className="wb-swatch measured" />
              measured
            </span>
            <span>
              <span className="wb-swatch reached" />
              came back
            </span>
            <span>
              <span className="wb-swatch unknown" />
              no flow attribution for these days, so the stage is unknown rather
              than zero
            </span>
          </div>
        </div>
      </section>

      <div className="wb-cols">
        <section className="wb-card">
          <div className="wb-card-hd">
            <h2>Who to act on</h2>
            <span className="wb-card-hint">a list is actionable, a rate is not</span>
          </div>
          <div className="wb-card-bd">
            {segmentsOf(totals, measurable).map((s) => (
              <div className={s.hot ? "wb-seg hot" : "wb-seg"} key={s.name}>
                <span className="wb-seg-nm">{s.name}</span>
                <span className="wb-seg-hint">{s.hint}</span>
                {s.measurable ? (
                  <span className="wb-seg-val">{num(s.value)}</span>
                ) : (
                  <span className="wb-seg-val unknown">unknown</span>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="wb-card">
          <div className="wb-card-hd">
            <h2>Reactivation by strategy</h2>
            <span className="wb-card-hint">global_blend sends identical slates</span>
          </div>
          <div className="wb-card-bd">
            {byStrategy.length === 0 && (
              <div className="wb-empty">No campaigns in this window.</div>
            )}
            <div className="wb-bars">
              {byStrategy.map((s) => (
                <div className="wb-bar-row" key={s.strategy}>
                  <span className="wb-bar-nm">{s.strategy}</span>
                  <span className="wb-bar-trk">
                    <span
                      className="wb-bar-fill"
                      style={{ width: `${Math.min(100, (s.rate ?? 0) * 100)}%` }}
                    />
                  </span>
                  <span className="wb-bar-val">
                    {pct(s.rate)} <span className="wb-dim">({num(s.cohort)})</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <section className="wb-card">
        <div className="wb-card-hd">
          <h2>Campaign days</h2>
          <span className="wb-card-hint">
            recency is how long since the last order when we emailed - a small
            number means we targeted an active customer
          </span>
        </div>
        <div className="wb-card-bd">
          <div className="wb-scroll">
            <table className="wb-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Strategy</th>
                  <th>Arm</th>
                  <th className="n">Matched</th>
                  <th className="n">Emailed</th>
                  <th className="n">Delivered</th>
                  {/* Named explicitly: this excludes image-proxy prefetches, so it
                      reads LOWER than the open count Klaviyo's own dashboard
                      shows. An unqualified "Opened" invites that comparison. */}
                  <th className="n">Opened (human)</th>
                  <th className="n">Clicked</th>
                  <th className="n">Came back</th>
                  <th className="n">Revenue</th>
                  <th className="n">Match rate</th>
                  <th className="n">Recency</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((r, i) => {
                  const rowMeasurable = rowEngagementIsMeasurable(r);
                  return (
                  <tr key={`${r.group_date}-${r.strategy}-${r.holdout_arm}-${i}`}>
                    <td className="n">{r.group_date}</td>
                    <td>{r.strategy}</td>
                    <td>
                      <ArmPill arm={r.holdout_arm} />
                    </td>
                    <td className="n">{num(r.customers_matched)}</td>
                    <td className="n">{num(r.pushed)}</td>
                    {/* n/a, not 0: an unattributable day did not deliver nothing,
                        we simply cannot say. */}
                    <td className="n">{attributed(r.delivered, rowMeasurable)}</td>
                    <td className="n">{attributed(r.opened_human, rowMeasurable)}</td>
                    <td className="n">{attributed(r.clicked, rowMeasurable)}</td>
                    <td className="n">{num(r.reactivated)}</td>
                    <td className="n">{money(r.attributed_revenue)}</td>
                    <td className="n">{pct(r.profile_match_rate)}</td>
                    <td className="n">{dayCount(r.recency_days_at_send_p50)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Rendered verbatim. Each one is a measured way the numbers above under-
          or overstate reality. */}
      <section className="wb-card">
        <div className="wb-card-hd">
          <h2>Read this with the numbers</h2>
        </div>
        <div className="wb-card-bd">
          <ul className="wb-caveats">
            {caveats.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}

function ArmPill({ arm }: { arm: string | null }) {
  if (arm === "treatment") return <span className="wb-pill treat">emailed</span>;
  if (arm === "control") return <span className="wb-pill ctrl">held back</span>;
  // Sends written before the arm dimension existed. Left visibly distinct rather
  // than shown as treatment, so they are never counted into a lift claim.
  return <span className="wb-pill">no arm</span>;
}

function Kpi({
  label,
  value,
  sub,
  hero,
}: {
  label: string;
  value: string;
  sub?: string;
  hero?: boolean;
}) {
  return (
    <div className={hero ? "wb-kpi hero" : "wb-kpi"}>
      <div className="wb-kpi-lbl">{label}</div>
      <div className="wb-kpi-val">{value}</div>
      {sub && <div className="wb-kpi-sub">{sub}</div>}
    </div>
  );
}

function groupByStrategy(rows: WinbackCampaignRow[]) {
  const acc = new Map<string, { cohort: number; reactivated: number }>();
  for (const r of rows) {
    const key = r.strategy ?? "unknown";
    const cur = acc.get(key) ?? { cohort: 0, reactivated: 0 };
    // Denominated on the measured cohort, matching how Gold gates the numerator.
    cur.cohort += r.measured_cohort ?? 0;
    cur.reactivated += r.reactivated ?? 0;
    acc.set(key, cur);
  }
  return Array.from(acc.entries())
    .map(([strategy, v]) => ({
      strategy,
      cohort: v.cohort,
      // Null, not zero, when this strategy targeted nobody.
      rate: v.cohort ? v.reactivated / v.cohort : null,
    }))
    .sort((a, b) => (b.rate ?? -1) - (a.rate ?? -1));
}
